import { UserRole, type RoleEnum } from '@/types/roles'
import {
  DashboardIcon,
  ClassesIcon,
  CalendarIcon,
  PackageIcon,
  UsersIcon,
  CustomersIcon,
  ReferralIcon,
  ReportsIcon,
} from '@/components/icons/AdminIcons'
import type { AppRoute } from '@/types/routes'
import type { RouteRecordWithMeta } from './types'
import type { RouteRecordRaw } from 'vue-router'

type AccessRoute = RouteRecordWithMeta & { children?: AccessRoute[] }

// Shared dynamic import for admin panel
const AdminPanel = () => import('@/views/admin_panel/AdminPanel.vue')

export type AdminRoute = AppRoute

export const AdminPages: Record<string, RouteRecordWithMeta> = {
  Dashboard: {
    path: '/admin',
    name: 'admin',
    component: AdminPanel,
    meta: {
      requiresAuth: true,
      isSystemUser: true,
      accessibleBy: [UserRole.ADMIN, UserRole.INSTRUCTOR, UserRole.COACH],
      label: 'Dashboard',
      icon: DashboardIcon,
      hideNavbar: true,
    },
  },
  Customers: {
    path: '/admin/customers',
    name: 'admin-customers',
    component: AdminPanel,
    meta: {
      requiresAuth: true,
      isSystemUser: true,
      accessibleBy: [UserRole.ADMIN, UserRole.COACH],
      label: 'Customers',
      icon: CustomersIcon,
      hideNavbar: true,
    },
  },
  Customers2: {
    path: '/admin/customers2',
    name: 'admin-customers2',
    component: AdminPanel,
    meta: {
      requiresAuth: true,
      isSystemUser: true,
      accessibleBy: [UserRole.ADMIN, UserRole.COACH],
      label: 'Customers New',
      icon: CustomersIcon,
      hideNavbar: true,
    },
  },
  CustomerProfile: {
    path: '/admin/customers/:id',
    name: 'admin-customer-profile',
    component: AdminPanel,
    meta: {
      requiresAuth: true,
      isSystemUser: true,
      accessibleBy: [UserRole.ADMIN, UserRole.COACH],
      label: 'Customer Profile',
      icon: CustomersIcon,
      hideNavbar: true,
    },
  },
  CustomerInformation: {
    path: '/admin/customers/:id/profile',
    name: 'admin-customer-profile-info',
    component: AdminPanel,
    meta: {
      requiresAuth: true,
      isSystemUser: true,
      accessibleBy: [UserRole.ADMIN, UserRole.COACH],
      label: 'Customer Information',
      icon: CustomersIcon,
      hideNavbar: true,
    },
  },
  CustomerActivity: {
    path: '/admin/customers/:id/activity',
    name: 'admin-customer-profile-activity',
    component: AdminPanel,
    meta: {
      requiresAuth: true,
      isSystemUser: true,
      accessibleBy: [UserRole.ADMIN, UserRole.COACH],
      label: 'Customer Activity',
      icon: CustomersIcon,
      hideNavbar: true,
    },
  },
  CustomerEnrolledSchedules: {
    path: '/admin/customers/:id/enrolled-schedules',
    name: 'admin-customer-profile-enrolled-schedules',
    component: AdminPanel,
    meta: {
      requiresAuth: true,
      isSystemUser: true,
      accessibleBy: [UserRole.ADMIN, UserRole.COACH],
      label: 'Customer Enrolled Schedules',
      icon: CustomersIcon,
      hideNavbar: true,
    },
  },
  CustomerSurvey: {
    path: '/admin/customers/:id/survey',
    name: 'admin-customer-profile-survey',
    component: AdminPanel,
    meta: {
      requiresAuth: true,
      isSystemUser: true,
      accessibleBy: [UserRole.ADMIN, UserRole.COACH],
      label: 'Customer Survey',
      icon: CustomersIcon,
      hideNavbar: true,
    },
  },
  CustomerConsultationNotes: {
    path: '/admin/customers/:id/consultation-notes',
    name: 'admin-customer-profile-consultation-notes',
    component: AdminPanel,
    meta: {
      requiresAuth: true,
      isSystemUser: true,
      accessibleBy: [UserRole.ADMIN, UserRole.COACH],
      label: 'Customer Consultation Notes',
      icon: CustomersIcon,
      hideNavbar: true,
    },
  },
  CustomerConsultations: {
    path: '/admin/customers/:id/consultations',
    name: 'admin-customer-profile-consultations',
    component: AdminPanel,
    meta: {
      requiresAuth: true,
      isSystemUser: true,
      accessibleBy: [UserRole.ADMIN, UserRole.COACH],
      label: 'Customer Consultations',
      icon: CustomersIcon,
      hideNavbar: true,
    },
  },
  CustomerBookFollowup: {
    path: '/admin/customers/:id/consultations/book-followup',
    name: 'admin-customer-book-followup',
    component: AdminPanel,
    meta: {
      requiresAuth: true,
      isSystemUser: true,
      accessibleBy: [UserRole.ADMIN, UserRole.COACH],
      label: 'Book Follow-up',
      icon: CustomersIcon,
      hideNavbar: true,
    },
  },
  CustomerDataSync: {
    path: '/admin/customers/:id/sync',
    name: 'admin-customer-profile-sync',
    component: AdminPanel,
    meta: {
      requiresAuth: true,
      isSystemUser: true,
      accessibleBy: [UserRole.ADMIN, UserRole.COACH],
      label: 'Customer Data Sync',
      icon: CustomersIcon,
      hideNavbar: true,
    },
  },
  CustomerReminders: {
    path: '/admin/customers/:id/reminders',
    name: 'admin-customer-profile-reminders',
    component: AdminPanel,
    meta: {
      requiresAuth: true,
      isSystemUser: true,
      accessibleBy: [UserRole.ADMIN, UserRole.COACH],
      label: 'Customer Reminders',
      icon: CustomersIcon,
      hideNavbar: true,
    },
  },
  CustomerNotificationDelivery: {
    path: '/admin/customers/:id/notification-delivery',
    name: 'admin-customer-profile-notification-delivery',
    component: AdminPanel,
    meta: {
      requiresAuth: true,
      isSystemUser: true,
      accessibleBy: [UserRole.ADMIN, UserRole.COACH],
      label: 'Notification delivery',
      icon: CustomersIcon,
      hideNavbar: true,
    },
  },
  CustomerSignupDebug: {
    path: '/admin/customers/:id/signup-debug',
    name: 'admin-customer-profile-signup-debug',
    component: AdminPanel,
    meta: {
      requiresAuth: true,
      isSystemUser: true,
      accessibleBy: [UserRole.ADMIN, UserRole.COACH],
      label: 'Signup Debug',
      icon: CustomersIcon,
      hideNavbar: true,
    },
  },
  CustomerFirebaseTools: {
    path: '/admin/customers/:id/firebase-tools',
    name: 'admin-customer-profile-firebase-tools',
    component: AdminPanel,
    meta: {
      requiresAuth: true,
      isSystemUser: true,
      accessibleBy: [UserRole.ADMIN],
      label: 'Customer Firebase Tools',
      icon: CustomersIcon,
      hideNavbar: true,
    },
  },
  CustomerCustomClaims: {
    path: '/admin/customers/:id/custom-claims',
    name: 'admin-customer-profile-custom-claims',
    component: AdminPanel,
    meta: {
      requiresAuth: true,
      isSystemUser: true,
      accessibleBy: [UserRole.ADMIN],
      label: 'Customer Custom Claims',
      icon: CustomersIcon,
      hideNavbar: true,
    },
  },
  CustomerPaymentMethodInputs: {
    path: '/admin/customers/:id/payment-method-inputs',
    name: 'admin-customer-profile-payment-method-inputs',
    component: AdminPanel,
    meta: {
      requiresAuth: true,
      isSystemUser: true,
      accessibleBy: [UserRole.ADMIN],
      label: 'Customer Payment Method Inputs',
      icon: CustomersIcon,
      hideNavbar: true,
    },
  },
  CustomerAuth: {
    path: '/admin/customers/:id/auth',
    name: 'admin-customer-profile-auth',
    component: AdminPanel,
    meta: {
      requiresAuth: true,
      isSystemUser: true,
      accessibleBy: [UserRole.ADMIN],
      label: 'Customer Auth',
      icon: CustomersIcon,
      hideNavbar: true,
    },
  },
  SystemUsers: {
    path: '/admin/system-users',
    name: 'admin-system-users',
    component: AdminPanel,
    meta: {
      requiresAuth: true,
      isSystemUser: true,
      accessibleBy: [UserRole.ADMIN],
      label: 'System Users',
      icon: UsersIcon,
      hideNavbar: true,
    },
  },
  Instructors: {
    path: '/admin/instructors',
    name: 'admin-instructors',
    component: AdminPanel,
    meta: {
      requiresAuth: true,
      isSystemUser: true,
      accessibleBy: [UserRole.ADMIN, UserRole.INSTRUCTOR, UserRole.COACH],
      label: 'Instructors',
      icon: UsersIcon,
      hideNavbar: true,
    },
  },
  Consultants: {
    path: '/admin/consultants',
    name: 'admin-consultants',
    component: AdminPanel,
    meta: {
      requiresAuth: true,
      isSystemUser: true,
      accessibleBy: [UserRole.ADMIN],
      label: 'Consultants',
      icon: UsersIcon,
      hideNavbar: true,
    },
  },
  ConsultantProfile: {
    path: '/admin/consultants/:id',
    name: 'admin-consultant-profile',
    component: AdminPanel,
    meta: {
      requiresAuth: true,
      isSystemUser: true,
      accessibleBy: [UserRole.ADMIN],
      label: 'Consultant Profile',
      icon: UsersIcon,
      hideNavbar: true,
    },
  },
  Consultations: {
    path: '/admin/consultations',
    name: 'admin-consultations',
    component: AdminPanel,
    meta: {
      requiresAuth: true,
      isSystemUser: true,
      accessibleBy: [UserRole.ADMIN],
      label: 'Consultations',
      icon: UsersIcon,
      hideNavbar: true,
    },
  },
  ConsultationProfile: {
    path: '/admin/consultations/:id',
    name: 'admin-consultation-profile',
    component: AdminPanel,
    meta: {
      requiresAuth: true,
      isSystemUser: true,
      accessibleBy: [UserRole.ADMIN],
      label: 'Consultation Profile',
      icon: UsersIcon,
      hideNavbar: true,
    },
  },
  ConsultationCarePlan: {
    path: '/admin/consultations/:id/care-plan',
    name: 'admin-consultation-care-plan',
    component: AdminPanel,
    meta: {
      requiresAuth: true,
      isSystemUser: true,
      accessibleBy: [UserRole.ADMIN],
      label: 'Consultation Care Plan',
      icon: UsersIcon,
      hideNavbar: true,
    },
  },
  ConsultationBookingDebug: {
    path: '/admin/consultations/:id/booking-debug',
    name: 'admin-consultation-booking-debug',
    component: AdminPanel,
    meta: {
      requiresAuth: true,
      isSystemUser: true,
      accessibleBy: [UserRole.ADMIN],
      label: 'Consultation Booking Debug',
      icon: UsersIcon,
      hideNavbar: true,
    },
  },
  ConsultationRelatedResources: {
    path: '/admin/consultations/:id/related-resources',
    name: 'admin-consultation-related-resources',
    component: AdminPanel,
    meta: {
      requiresAuth: true,
      isSystemUser: true,
      accessibleBy: [UserRole.ADMIN],
      label: 'Consultation Related Resources',
      icon: UsersIcon,
      hideNavbar: true,
    },
  },
  InstructorProfile: {
    path: '/admin/instructors/:id',
    name: 'admin-instructor-profile',
    component: AdminPanel,
    meta: {
      requiresAuth: true,
      isSystemUser: true,
      accessibleBy: [UserRole.ADMIN, UserRole.INSTRUCTOR, UserRole.COACH],
      label: 'Instructor Profile',
      icon: UsersIcon,
      hideNavbar: true,
    },
  },
  Classes: {
    path: '/admin/classes',
    name: 'admin-classes',
    component: AdminPanel,
    meta: {
      requiresAuth: true,
      isSystemUser: true,
      accessibleBy: [UserRole.ADMIN, UserRole.INSTRUCTOR, UserRole.COACH],
      label: 'Classes',
      icon: ClassesIcon,
      hideNavbar: true,
    },
  },
  ClassProfile: {
    path: '/admin/classes/:id',
    name: 'admin-class-profile',
    component: AdminPanel,
    meta: {
      requiresAuth: true,
      isSystemUser: true,
      accessibleBy: [UserRole.ADMIN, UserRole.INSTRUCTOR, UserRole.COACH],
      label: 'Class Profile',
      icon: ClassesIcon,
      hideNavbar: true,
    },
  },
  UpcomingClasses: {
    path: '/admin/upcoming-classes',
    name: 'admin-upcoming-classes',
    component: AdminPanel,
    meta: {
      requiresAuth: true,
      isSystemUser: true,
      accessibleBy: [UserRole.ADMIN, UserRole.INSTRUCTOR, UserRole.COACH],
      label: 'Upcoming Classes',
      icon: CalendarIcon,
      hideNavbar: true,
    },
  },
  PastClasses: {
    path: '/admin/past-classes',
    name: 'admin-past-classes',
    component: AdminPanel,
    meta: {
      requiresAuth: true,
      isSystemUser: true,
      accessibleBy: [UserRole.ADMIN, UserRole.INSTRUCTOR, UserRole.COACH],
      label: 'Past Classes',
      icon: CalendarIcon,
      hideNavbar: true,
    },
  },
  ClassInstanceProfile: {
    path: '/admin/past-classes/:id',
    name: 'admin-class-instance-profile',
    component: AdminPanel,
    meta: {
      requiresAuth: true,
      isSystemUser: true,
      accessibleBy: [UserRole.ADMIN, UserRole.INSTRUCTOR, UserRole.COACH],
      label: 'Class Instance Profile',
      icon: CalendarIcon,
      hideNavbar: true,
    },
  },
  ClassInstanceProfileDetails: {
    path: '/admin/past-classes/:id/details',
    name: 'admin-class-instance-profile-details',
    component: AdminPanel,
    meta: {
      requiresAuth: true,
      isSystemUser: true,
      accessibleBy: [UserRole.ADMIN, UserRole.INSTRUCTOR, UserRole.COACH],
      label: 'Class Instance Profile Details',
      icon: CalendarIcon,
      hideNavbar: true,
    },
  },
  ClassInstanceProfileRecording: {
    path: '/admin/past-classes/:id/recording',
    name: 'admin-class-instance-profile-recording',
    component: AdminPanel,
    meta: {
      requiresAuth: true,
      isSystemUser: true,
      accessibleBy: [UserRole.ADMIN, UserRole.INSTRUCTOR, UserRole.COACH],
      label: 'Class Instance Profile Recording',
      icon: CalendarIcon,
      hideNavbar: true,
    },
  },
  StripeLists: {
    path: '/admin/stripe-lists',
    name: 'admin-stripe-lists',
    component: AdminPanel,
    meta: {
      requiresAuth: true,
      isSystemUser: true,
      accessibleBy: [UserRole.ADMIN],
      label: 'Stripe Products & Coupons',
      icon: PackageIcon,
      hideNavbar: true,
    },
  },
  StripeVendorCollections: {
    path: '/admin/stripe-vendor-collections',
    name: 'admin-stripe-vendor-collections',
    component: AdminPanel,
    meta: {
      requiresAuth: true,
      isSystemUser: true,
      accessibleBy: [UserRole.ADMIN],
      label: 'Stripe Vendor Collections',
      icon: PackageIcon,
      hideNavbar: true,
    },
  },
  Recordings: {
    path: '/admin/recordings',
    name: 'admin-recordings',
    component: AdminPanel,
    meta: {
      requiresAuth: true,
      isSystemUser: true,
      accessibleBy: [UserRole.ADMIN, UserRole.INSTRUCTOR, UserRole.COACH],
      label: 'Recordings',
      icon: PackageIcon,
      hideNavbar: true,
    },
  },
  TellescopeVendorSync: {
    path: '/admin/tellescope-vendor-sync',
    name: 'admin-tellescope-vendor-sync',
    component: AdminPanel,
    meta: {
      requiresAuth: true,
      isSystemUser: true,
      accessibleBy: [UserRole.ADMIN],
      label: 'Tellescope Vendor Sync',
      icon: UsersIcon,
      hideNavbar: true,
    },
  },
  ClinicalConsultationTest: {
    path: '/admin/clinical-consultation-test',
    name: 'admin-clinical-consultation-test',
    redirect: {
      name: 'admin-clinical-consultation-test-object',
      params: { objectType: 'consultation' },
    },
    meta: {
      requiresAuth: true,
      isSystemUser: true,
      accessibleBy: [UserRole.ADMIN],
      label: 'Clinical Consultation Test (Patient / Provider)',
      icon: UsersIcon,
      hideNavbar: true,
    },
  },
  ClinicalConsultationTestObject: {
    path: '/admin/clinical-consultation-test/:objectType(consultation|patient|consultant)/:segment1?/:segment2?/:segment3?',
    name: 'admin-clinical-consultation-test-object',
    component: AdminPanel,
    meta: {
      requiresAuth: true,
      isSystemUser: true,
      accessibleBy: [UserRole.ADMIN],
      label: 'Clinical Consultation Test (Patient / Provider)',
      icon: UsersIcon,
      hideNavbar: true,
    },
  },
  Reports: {
    path: '/admin/reports',
    name: 'admin-reports',
    component: AdminPanel,
    meta: {
      requiresAuth: true,
      isSystemUser: true,
      accessibleBy: [UserRole.ADMIN, UserRole.INSTRUCTOR, UserRole.COACH],
      label: 'Reports',
      icon: ReportsIcon,
      hideNavbar: true,
    },
  },
  ReportsDetail: {
    path: '/admin/reports/:path*',
    name: 'admin-reports-detail',
    component: AdminPanel,
    meta: {
      requiresAuth: true,
      isSystemUser: true,
      accessibleBy: [UserRole.ADMIN, UserRole.INSTRUCTOR, UserRole.COACH],
      label: 'Report Detail',
      icon: ReportsIcon,
      hideNavbar: true,
    },
  },
  ReferralPrograms: {
    path: '/admin/referral-programs',
    name: 'admin-referral-programs',
    component: AdminPanel,
    meta: {
      requiresAuth: true,
      isSystemUser: true,
      accessibleBy: [UserRole.ADMIN],
      label: 'Referral Programs',
      icon: ReferralIcon,
      hideNavbar: true,
    },
    children: [
      {
        path: '',
        name: 'admin-referral-programs-list',
        component: () => import('@/components/admin/referralPrograms/ReferralProgramsListView.vue'),
        meta: {
          requiresAuth: true,
          isSystemUser: true,
          accessibleBy: [UserRole.ADMIN],
          label: 'Referral Programs List',
          icon: ReferralIcon,
        },
      },
      {
        path: 'create',
        name: 'admin-referral-programs-create',
        component: () =>
          import('@/components/admin/referralPrograms/ReferralProgramCreateView.vue'),
        meta: {
          requiresAuth: true,
          isSystemUser: true,
          accessibleBy: [UserRole.ADMIN],
          label: 'Create Referral Program',
          icon: ReferralIcon,
        },
      },
      {
        path: ':id',
        name: 'admin-referral-programs-profile',
        component: () =>
          import('@/components/admin/referralPrograms/ReferralProgramProfileView.vue'),
        meta: {
          requiresAuth: true,
          isSystemUser: true,
          accessibleBy: [UserRole.ADMIN],
          label: 'Referral Program Profile',
          icon: ReferralIcon,
        },
      },
      {
        path: ':id/edit',
        name: 'admin-referral-programs-edit',
        component: () => import('@/components/admin/referralPrograms/ReferralProgramEditView.vue'),
        meta: {
          requiresAuth: true,
          isSystemUser: true,
          accessibleBy: [UserRole.ADMIN],
          label: 'Edit Referral Program',
          icon: ReferralIcon,
        },
      },
      {
        path: ':id/apply',
        name: 'admin-referral-programs-apply',
        component: () => import('@/components/admin/referralPrograms/ReferralProgramApplyView.vue'),
        meta: {
          requiresAuth: true,
          isSystemUser: true,
          accessibleBy: [UserRole.ADMIN],
          label: 'Add Users',
          icon: ReferralIcon,
        },
      },
    ],
  },
}

const adminPages = Object.values(AdminPages) as AccessRoute[]
export const adminRoutes = adminPages as RouteRecordRaw[]

// Helper functions for route access control
export function isRouteAccessibleForRole(routePath: string, userRoles: RoleEnum[]): boolean {
  const clinicalConsultationTestBase = AdminPages.ClinicalConsultationTest.path
  if (
    routePath === clinicalConsultationTestBase ||
    routePath.startsWith(`${clinicalConsultationTestBase}/`)
  ) {
    const route = AdminPages.ClinicalConsultationTestObject
    if (userRoles.includes(UserRole.ADMIN)) return true
    return userRoles.some((role) => route.meta?.accessibleBy?.includes(role) ?? false)
  }

  // Find the route definition (check both parent and child routes)
  const findRoute = (routes: AccessRoute[], parentPath = ''): AccessRoute | undefined => {
    for (const r of routes) {
      // Build the full path for this route
      const fullPath = parentPath + (r.path.startsWith('/') ? r.path : '/' + r.path)

      // Check if this route matches exactly
      if (fullPath === routePath) return r

      // For routes with parameters, check if the path pattern matches
      if (r.path.includes(':')) {
        const routePattern = fullPath.replace(/:[^/]+/g, '[^/]+')
        const regex = new RegExp(`^${routePattern}$`)
        if (regex.test(routePath)) return r
      }

      // Check child routes if they exist
      if (r.children) {
        const childRoute = findRoute(r.children, fullPath)
        if (childRoute) return childRoute
      }
    }
    return undefined
  }

  const route = findRoute(adminPages)

  if (!route || !route.meta?.accessibleBy) return false

  // Admin has access to all routes
  if (userRoles.includes(UserRole.ADMIN)) return true

  // Check if any of the user's roles are in the accessibleBy array
  return userRoles.some((role) => route.meta?.accessibleBy?.includes(role) ?? false)
}
